/*
 * PDP purchase gate: serial number entry + purchase agreement checkbox.
 * Markup: snippets/pdp-serial-number.liquid, snippets/pdp-purchase-agreement.liquid.
 *
 * Both elements carry data-form (the product form's id) and data-section. The gate for that
 * form is evaluated as a whole, in the live theme's order:
 *   1. Serial box present and empty (or whitespace) -> the form's buttons (Add to Cart, Buy it
 *      now) are hidden, and the agreement is hidden too.
 *   2. Agreement present and unticked -> Add to Cart is disabled, Buy it now hidden.
 *
 * Disabling Add to Cart has to coexist with Dawn: assets/product-info.js re-enables or
 * disables it on every variant change (sold out / available). So the gate remembers whether
 * Dawn had it disabled before the gate touched it, never enables a button Dawn disabled, and
 * re-applies itself after PUB_SUB_EVENTS.variantChange (published after Dawn's toggle).
 *
 * A capture-phase submit listener on the form is the backstop, so nothing reaches
 * assets/product-form.js's submit handler while the gate is closed.
 *
 * The file may be loaded twice (one <script> per snippet); everything is guarded.
 */
if (!customElements.get('uhs-serial-number')) {
  (() => {
    const boundForms = new WeakSet();

    const getParts = (formId) => {
      const form = document.getElementById(formId);
      if (!form) return null;
      const serial = document.querySelector(`uhs-serial-number[data-form="${formId}"] .uhs-serial__input`);
      const agreement = document.querySelector(`uhs-purchase-agreement[data-form="${formId}"]`);
      return {
        form,
        serial,
        agreement,
        checkbox: agreement?.querySelector('.uhs-agreement__input'),
        buttons: form.querySelector('.product-form__buttons'),
        submit: form.querySelector('[type="submit"][name="add"]'),
        payment: form.querySelector('.shopify-payment-button'),
      };
    };

    const getState = (parts) => {
      const serialOk = !parts.serial || parts.serial.value.trim() !== '';
      const agreementOk = !parts.checkbox || parts.checkbox.checked;
      return { serialOk, agreementOk };
    };

    const setSubmitGated = (submit, gated) => {
      if (!submit) return;
      if (gated) {
        if (!submit.dataset.uhsGate) submit.dataset.uhsGate = submit.hasAttribute('disabled') ? 'dawn' : 'gate';
        submit.setAttribute('disabled', 'disabled');
      } else if (submit.dataset.uhsGate) {
        if (submit.dataset.uhsGate === 'gate') submit.removeAttribute('disabled');
        delete submit.dataset.uhsGate;
      }
    };

    const update = (formId, sectionId) => {
      const parts = getParts(formId);
      if (!parts) return;
      if (sectionId) bindForm(formId, sectionId, parts.form);

      const { serialOk, agreementOk } = getState(parts);
      parts.buttons?.classList.toggle('hidden', !serialOk);
      parts.agreement?.classList.toggle('hidden', !serialOk);
      parts.agreement?.classList.toggle('is-checked', agreementOk);
      parts.payment?.classList.toggle('hidden', !agreementOk);
      setSubmitGated(parts.submit, !agreementOk);
    };

    // Once per form, not per element: with both elements on the page, a second variantChange
    // subscriber would see the first one's `disabled` and record it as Dawn's.
    function bindForm(formId, sectionId, form) {
      if (boundForms.has(form)) return;
      boundForms.add(form);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== sectionId) return;
          // Dawn has just set Add to Cart's disabled state for the new variant; take that as
          // the baseline before gating again.
          const submit = document.getElementById(formId)?.querySelector('[type="submit"][name="add"]');
          if (submit) delete submit.dataset.uhsGate;
          update(formId);
        });
      }

      form.addEventListener(
        'submit',
        (event) => {
          const parts = getParts(formId);
          if (!parts) return;
          const { serialOk, agreementOk } = getState(parts);
          if (serialOk && agreementOk) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          (serialOk ? parts.checkbox : parts.serial)?.focus();
        },
        true,
      );
    }

    class GateElement extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.form;
        this.onChange = () => update(this.formId);
        this.addEventListener('input', this.onChange);
        this.addEventListener('change', this.onChange);
        update(this.formId, this.dataset.section);
      }

      disconnectedCallback() {
        this.removeEventListener('input', this.onChange);
        this.removeEventListener('change', this.onChange);
      }
    }

    customElements.define('uhs-serial-number', class extends GateElement {});
    if (!customElements.get('uhs-purchase-agreement')) {
      customElements.define('uhs-purchase-agreement', class extends GateElement {});
    }
  })();
}
